import { useState, useCallback, useMemo } from "react";

import { subMonths, format } from "date-fns";

import { useObjectMemo } from "../../../hooks";
import { useTransactionsForCard } from "../../entities/subscriptions";
import {
  MONTH_LABELS_SMALL,
  MONTH_LABELS_LARGE,
  MONTH_FULL_NAME,
} from "../../entities/subscriptions/constants";

import { generateLastNYearMonths } from "./utils";

import type { DataPoint } from "../../entities/subscriptions/types";

export const useCurrentMonth = (): DateString => {
  return useMemo(() => format(new Date(), "yyyy-MM-dd") as DateString, []);
};

export const use13MonthsAgo = (): DateString => {
  return useMemo(
    () => format(subMonths(new Date(), 13), "yyyy-MM-dd") as DateString,
    [],
  );
};

export const useSubscriptionPaymentHistory = (
  cardId: API.CardId,
  subscriptionId: string,
) => {
  const subscriptionsForMonths = useTransactionsForCard(cardId);
  const paymentHistory = useMemo(() => {
    if (!subscriptionsForMonths) {
      return [];
    }

    const transactionsForSubscription: API.CardTransaction[] = [];

    subscriptionsForMonths.data.forEach(({ transactions }) => {
      transactions.forEach((transaction) => {
        if (subscriptionId === transaction.subscription_id) {
          transactionsForSubscription.push(transaction);
        }
      });
    });

    return transactionsForSubscription.reverse();
  }, [subscriptionsForMonths, subscriptionId]);

  return useObjectMemo({
    paymentHistory,
  });
};

const isMonthString = (
  month: string | YearMonthString,
): month is YearMonthString => {
  // Strict YYYY-MM where YYYY is 0001–9999 and MM is 01–12
  return /^(?!0000)\d{4}-(0[1-9]|1[0-2])$/.test(month);
};

const getMonthLabelBySize = ({
  size,
  month,
}: {
  size: string;
  month: string | YearMonthString;
}): string => {
  const monthLabelLookup = ["xs", "s"].includes(size)
    ? MONTH_LABELS_SMALL
    : MONTH_LABELS_LARGE;

  if (isMonthString(month)) {
    const monthNumber = month.slice(5) as keyof typeof monthLabelLookup;
    return monthLabelLookup[monthNumber];
  }

  return "";
};

// Generate 12 months worth of "YYYY-MM" (oldest → newest).
const DEFAULT_MONTHS: YearMonthString[] = generateLastNYearMonths(12);

const DEFAULT_DATA: { data: API.CardTransactionsPerMonth[] } = {
  data: DEFAULT_MONTHS.map((m) => ({
    month: m,
    total: "0",
    transaction_count: 0,
    transactions: [],
  })),
};

export function useSubscriptionsChartData(cardId: API.CardId, size: string) {
  const [selectedMonth, setSelectedMonth] = useState<YearMonthString>(
    format(new Date(), "yyyy-MM") as YearMonthString,
  );

  const result = useTransactionsForCard(cardId);

  const transactionData: API.CardTransactionsPerMonth[] =
    !result?.data || result?.data.length === 0
      ? DEFAULT_DATA.data
      : result.data;

  const data: DataPoint[] = useMemo(() => {
    return (transactionData ?? []).map(({ month, total }) => ({
      month,
      total: total ? parseFloat(total) : 0,
    }));
  }, [transactionData]);

  const selectedMonthIndex = data.findIndex(
    (datum) => datum.month === selectedMonth,
  );
  const prevMonth =
    selectedMonthIndex > 0 ? data[selectedMonthIndex - 1].month : null;
  const nextMonth =
    selectedMonthIndex < data.length - 1
      ? data[selectedMonthIndex + 1].month
      : null;
  const year = selectedMonth.slice(0, 4);
  const prev12MonthsTotal = useMemo(
    () =>
      data.slice(1, 12).reduce((sum, dataForMonth) => {
        return sum + dataForMonth.total;
      }, 0),
    [data],
  );
  const monthTotal = data[selectedMonthIndex]?.total ?? 0;

  const onMonthChange = useCallback(
    (m: YearMonthString) => setSelectedMonth(m),
    [],
  );

  const monthLabels = useMemo<Record<string, string>>(() => {
    return transactionData.reduce(
      (acc, { month }) => {
        acc[month] = getMonthLabelBySize({ month, size });
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [transactionData, size]);

  const selectedMonthFullName = useMemo(() => {
    return MONTH_FULL_NAME[selectedMonth.slice(5) as MonthString];
  }, [selectedMonth]);

  const isSelectedThisMonth = useMemo(
    () => selectedMonthIndex === data.length - 1,
    [selectedMonthIndex, data.length],
  );
  const selectedTransactions = useMemo(() => {
    return transactionData.find(
      (transaction) => transaction.month === selectedMonth,
      [selectedMonth],
    )?.transactions;
  }, [selectedMonth, transactionData]);

  return useObjectMemo({
    data,
    monthLabels,
    selectedMonth,
    prevMonth,
    nextMonth,
    year,
    prev12MonthsTotal,
    monthTotal,
    selectedMonthFullName,
    isSelectedThisMonth,
    onMonthChange,
    selectedTransactions,
    allTransactions: transactionData,
  });
}
